import { MDXEditor, type MDXEditorMethods } from '@mdxeditor/editor';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import { useColorScheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

import Typography from '@mui/material/Typography';
import { useAtom } from 'jotai';
import {
  type SyntheticEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useFormState,
  useWatch
} from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import CheckPermissions from '@/components/CheckPermissions';
import FileUploader from '@/components/FileUploader';
import { PERMISSIONS } from '@/constants/auth.constants';
import {
  getAttachmentUploaderProps,
  isKanatiServiceTicket,
  KANATI_MAX_TEXT_LENGTH,
  KANATI_TEXT_TOO_LONG_MESSAGE
} from '@/features/TicketCreateDialog/TicketCreateDialog.constants';
import { getReferenceTicketTemplate } from '@/features/TicketCreateDialog/TicketCreateDialog.utils';
import useConfirm from '@/hooks/useConfirm';
import { useFetchSettings } from '@/pages/Administration/Administration.store';
import { selectedOrganizationIdAtom } from '@/pages/Organizations';
import {
  EMPTY_TICKET_REFERENCE,
  TICKET_TYPE,
  ticketAtom,
  ticketDialogIsOpenAtom,
  ticketDialogReferenceTicketAtom,
  ticketDialogTemplateAtom,
  useFetchMessageTemplates
} from '@/pages/Ticket';
import { AI_SOC_TEAM_QUEUE } from '@/pages/Ticket/Ticket.constants';
import { useCreateMessage } from '@/pages/Ticket/Ticket.store';
import type { MessageCreateDTO } from '@/pages/Ticket/Ticket.types';
import { MDX_EDITOR_CONFIG } from '@/pages/Ticket/TicketCreate/TicketCreate.constants';
import TicketDetailsContext from '@/pages/Ticket/TicketDetails/TicketDetails.store';
import {
  TICKET_ORIGINATION_SOURCE,
  TICKET_STATUS
} from '@/pages/Tickets/Tickets.constants';
import { userAtom } from '@/store/user.store';
import KanatiToggle from './KanatiToggle';
import DEFAULT_VALUES, {
  KANATI_RESPONSE_VISIBILITY
} from './MessageInput.constants';
import useStyles from './MessageInput.styles';

const MessageInput = () => {
  const [selectedOrganizationId] = useAtom(selectedOrganizationIdAtom);
  const [ticket] = useAtom(ticketAtom);
  const { organization, id: ticket_id, ticket_status } = ticket;
  const [, setTicketDialogIsOpen] = useAtom(ticketDialogIsOpenAtom);
  const [ticketDialogReferenceTicket, setTicketDialogReferenceTicket] = useAtom(
    ticketDialogReferenceTicketAtom
  );
  const [, setTicketDialogTemplate] = useAtom(ticketDialogTemplateAtom);
  const [userData] = useAtom(userAtom);
  const [inputValue, setInputValue] = useState('');
  const [messageTemplateKey, setMessageTemplateKey] = useState(uuidv4());
  const { classes } = useStyles();
  const { data: messageTemplates, isFetching: isMessageTemplatesFetching } =
    useFetchMessageTemplates();
  const { data: settings, isSuccess: settingsResolved } = useFetchSettings(
    organization?.label
  );
  const isKanatiTicket = isKanatiServiceTicket(
    ticket.ticket_type,
    ticket.ticket_category?.id
  );
  const canSendToKanati =
    userData?.internal === true &&
    settings?.ai_enable_submission === true &&
    ((isKanatiTicket && !!organization?.label && settingsResolved) ||
      (ticket.ticket_type === TICKET_TYPE.THREAT &&
        ticket.team_queue === AI_SOC_TEAM_QUEUE));
  // the length limit only applies to external users; internal users (incl. Kanati) are exempt
  const isKanatiLimited = isKanatiTicket && userData?.internal !== true;
  const mdxRef = useRef<MDXEditorMethods>(null);
  const { mode: themeMode } = useColorScheme();

  const messageTemplateOptions = messageTemplates.map(
    (template) => template.template_name
  );

  //-----------------------------------------------------------------
  const form = useForm({
    defaultValues: DEFAULT_VALUES
  });
  const {
    control,
    handleSubmit,
    reset: resetForm,
    setValue,
    clearErrors
  } = form;
  const formTicketId = useWatch({ control, name: 'ticket_id' });
  const kanatiResponseVisibility = useWatch({
    control,
    name: 'kanati_response_visibility'
  });
  const isInternalMessage = useWatch({ control, name: 'internal' });
  const bodyLength = (useWatch({ control, name: 'body' }) ?? '').length;
  const { errors: bodyFormErrors } = useFormState({ control, name: 'body' });

  const reset = () => {
    resetForm({
      ...DEFAULT_VALUES,
      ticket_id
    });
    mdxRef.current?.setMarkdown('');
    setInputValue('');
    setMessageTemplateKey(uuidv4());
  };
  const { ConfirmationModal: HumanSocConfirmModal, confirm: confirmHumanSoc } =
    useConfirm({
      title: 'Engage human SOC',
      message: `A Pondurance SOC analyst will review this ticket and continue the conversation here.
      Kanati will pause while the Human SOC is engaged.`,
      button: 'Request Human SOC',
      buttonColor: 'primary'
    });
  const refetch = useContext(TicketDetailsContext);
  const { createMessage, isLoadingCreateMessage } = useCreateMessage(
    refetch,
    reset
  );

  const handleChange = (value: string) => {
    const template = messageTemplates.find((messageTemplate) => {
      return messageTemplate.template_name === value;
    });

    if (!template) return;
    const nextBody = (template.message as string) ?? '';
    setValue('body', nextBody, { shouldDirty: true });
    mdxRef.current?.setMarkdown(nextBody);
    // TODO: fix ts error
    // @ts-expect-error ts(18047)
    mdxRef.current.focus();
  };

  const handleCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTicketDialogReferenceTicket({
      ...EMPTY_TICKET_REFERENCE,
      checked: event.target.checked,
      original_ticket_id: ticket_id
    });
  };

  const handleHumanSocToggleChange = async (
    checked: boolean,
    onChange: (value: boolean) => void
  ) => {
    if (!checked) {
      onChange(false);
      return;
    }
    // keep the toggle off unless the customer confirms
    if (await confirmHumanSoc()) {
      onChange(true);
    }
  };

  const onSubmit: SubmitHandler<MessageCreateDTO> = (data) => {
    // if creating a reference ticket, open the ticket dialog with the reference ticket template
    if (ticketDialogReferenceTicket.checked) {
      setTicketDialogTemplate(getReferenceTicketTemplate(ticket, data));
      setTicketDialogIsOpen(true);
      setTicketDialogReferenceTicket({
        ...ticketDialogReferenceTicket,
        checked: false
      });
      reset();
      return;
    }
    createMessage(data);
  };

  useEffect(() => {
    setValue('kanati_response_visibility', null);
  }, [
    ticket_id,
    organization?.id,
    selectedOrganizationId,
    canSendToKanati,
    setValue
  ]);

  useEffect(() => {
    // set ticket_id in form when ticket_id changes
    if (ticket_id !== formTicketId) {
      setValue('ticket_id', ticket_id);
    }
  }, [ticket_id, formTicketId]);

  useEffect(() => {
    // uncheck checkbox if it was previously checked on another page or user changes orgs
    setTicketDialogReferenceTicket(EMPTY_TICKET_REFERENCE);
  }, [selectedOrganizationId]);

  useEffect(() => {
    // internal response is only available when the message itself is internal
    if (
      !isInternalMessage &&
      kanatiResponseVisibility === KANATI_RESPONSE_VISIBILITY.INTERNAL
    ) {
      setValue('kanati_response_visibility', KANATI_RESPONSE_VISIBILITY.PUBLIC);
    }
  }, [isInternalMessage, kanatiResponseVisibility]);

  const handleMessageChange = (value: string) => {
    clearErrors('body');
    setValue('body', value, { shouldDirty: true });
  };

  const showClosedAlert = useMemo(() => {
    return (
      [TICKET_STATUS.AUTO_CLOSED, TICKET_STATUS.COMPLETED].includes(
        (ticket_status?.name as TICKET_STATUS) || ''
      ) && ticket.origination_source !== TICKET_ORIGINATION_SOURCE.AI_SOC
    );
  }, [ticket_status]);

  return (
    <>
      {showClosedAlert && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This ticket is closed. New comments are not being monitored.
          {!userData?.internal &&
            'To re-engage the SOC for this issue, check the checkbox below to open a new ticket.'}
        </Alert>
      )}
      <HumanSocConfirmModal />
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack
            direction="row"
            spacing={{ xs: 0, sm: 1.5 }}
            sx={{ width: '100%' }}
          >
            <Avatar
              className={classes.messageInputAvatar}
              src={userData?.image_url || ''}
            >
              {userData?.userInitials}
            </Avatar>
            <Stack className={classes.messageInputContainer}>
              <Grid
                container
                className={classes.messageInputTextFieldContainer}
              >
                <Controller
                  control={control}
                  name="body"
                  rules={{
                    required: !ticketDialogReferenceTicket.checked,
                    validate: (value) =>
                      !isKanatiLimited ||
                      (value ?? '').length <= KANATI_MAX_TEXT_LENGTH ||
                      KANATI_TEXT_TOO_LONG_MESSAGE
                  }}
                  render={({ field, formState }) => (
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <MDXEditor
                        className={`${themeMode}-theme dark-editor ${classes.mdxEditor} ${formState.errors.body ? 'has-error' : ''}`}
                        contentEditableClassName="cm-richView"
                        ref={mdxRef}
                        markdown={field.value ?? ''}
                        onChange={handleMessageChange}
                        plugins={MDX_EDITOR_CONFIG}
                      />
                      {/* Kanati character count, anchored to the editor's bottom-right corner */}
                      {isKanatiLimited && (
                        <Typography
                          variant="caption"
                          color={
                            bodyLength > KANATI_MAX_TEXT_LENGTH
                              ? 'error'
                              : 'text.secondary'
                          }
                          sx={{
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            bottom: 8,
                            pointerEvents: 'none',
                            position: 'absolute',
                            px: 0.5,
                            right: 12
                          }}
                        >
                          {bodyLength}/{KANATI_MAX_TEXT_LENGTH}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              </Grid>
              <Grid container className={classes.messageInputOptionsContainer}>
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  spacing={1.5}
                  sx={{ width: '100%' }}
                >
                  <CheckPermissions permissions={PERMISSIONS.INTERNAL}>
                    <div className={classes.messageInputOptionsSection1}>
                      {!isMessageTemplatesFetching && (
                        <Autocomplete
                          key={messageTemplateKey}
                          className={classes.messageInputMessageTemplatesSelect}
                          fullWidth
                          disableClearable
                          inputValue={inputValue}
                          onInputChange={(
                            _event: SyntheticEvent<Element, Event>,
                            newInputValue: string
                          ) => {
                            setInputValue(newInputValue);
                          }}
                          options={messageTemplateOptions || []}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Templates" />
                          )}
                          onChange={(_, value) => {
                            handleChange(value);
                          }}
                        />
                      )}
                    </div>
                  </CheckPermissions>
                  <Box className={classes.messageInputOptionsSection2}>
                    {/* attachments */}
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexGrow: 1
                      }}
                    >
                      {(isKanatiTicket ||
                        kanatiResponseVisibility === null) && (
                        <FileUploader
                          {...getAttachmentUploaderProps(isKanatiTicket)}
                        />
                      )}
                    </Box>
                    {/* reference ticket checkbox */}
                    {organization?.id === selectedOrganizationId &&
                      (ticket_status?.name === TICKET_STATUS.COMPLETED ||
                        ticket_status?.name === TICKET_STATUS.AUTO_CLOSED) &&
                      kanatiResponseVisibility === null && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={ticketDialogReferenceTicket.checked}
                              color="primary"
                              onChange={(event) => handleCheckbox(event)}
                            />
                          }
                          label="Open New and Reference Ticket"
                          style={{
                            marginRight: 15,
                            whiteSpace: 'nowrap'
                          }}
                        />
                      )}
                    {/* request human SOC option (customers only) */}
                    {!userData?.internal && ticket.human_soc_requestable && (
                      <Controller
                        control={control}
                        name="request_human_soc"
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Switch
                                color="primary"
                                checked={field.value === true}
                                onChange={(event) =>
                                  handleHumanSocToggleChange(
                                    event.target.checked,
                                    field.onChange
                                  )
                                }
                              />
                            }
                            label="Request human SOC"
                            style={{
                              marginRight: 15,
                              whiteSpace: 'nowrap'
                            }}
                          />
                        )}
                      />
                    )}
                    {/* send to Kanati option */}
                    {canSendToKanati && (
                      <KanatiToggle clearAttachments={!isKanatiTicket} />
                    )}
                    {/* internal message switch */}
                    {userData?.internal && (
                      <FormControlLabel
                        control={
                          <Controller
                            control={control}
                            name="internal"
                            render={({ field }) => (
                              <Switch
                                color="primary"
                                checked={field.value}
                                {...field}
                              />
                            )}
                          />
                        }
                        label="Message is Internal"
                        sx={{
                          marginRight: 0,
                          whiteSpace: 'nowrap'
                        }}
                      />
                    )}
                    <Button
                      loading={isLoadingCreateMessage}
                      loadingPosition="center"
                      startIcon=""
                      type="submit"
                      variant="outlined"
                    >
                      Submit
                    </Button>
                  </Box>
                </Stack>
              </Grid>
            </Stack>
          </Stack>
          {bodyFormErrors.body?.message && (
            <FormHelperText error sx={{ mt: 0.5, ml: 7 }}>
              {bodyFormErrors.body.message}
            </FormHelperText>
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default MessageInput;
